import { CANVAS_SIZE } from "@mn/shared";

export interface Camera {
  x: number;
  y: number;
  z: number;
}
export const MIN_Z = 0.1;
export const MAX_Z = 4;
export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Screen (viewport-relative px) -> page coordinates. */
export const screenToPage = (c: Camera, sx: number, sy: number) => ({ x: sx / c.z - c.x, y: sy / c.z - c.y });
export const pageToScreen = (c: Camera, px: number, py: number) => ({
  x: (px + c.x) * c.z,
  y: (py + c.y) * c.z,
});

export function zoomAt(c: Camera, sx: number, sy: number, nextZ: number): Camera {
  const z = clamp(nextZ, MIN_Z, MAX_Z);
  const before = screenToPage(c, sx, sy);
  return { z, x: sx / z - before.x, y: sy / z - before.y };
}

export function clampCamera(c: Camera, vw: number, vh: number): Camera {
  const z = clamp(c.z, MIN_Z, MAX_Z);
  const slackX = (vw / z) * 0.5;
  const slackY = (vh / z) * 0.5;
  return {
    z,
    x: clamp(c.x, -(CANVAS_SIZE - vw / z + slackX), slackX),
    y: clamp(c.y, -(CANVAS_SIZE - vh / z + slackY), slackY),
  };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function zoomToBounds(b: Rect, vw: number, vh: number, inset = 80, maxZ = 1): Camera {
  const z = clamp(
    Math.min((vw - inset * 2) / Math.max(b.w, 1), (vh - inset * 2) / Math.max(b.h, 1), maxZ),
    MIN_Z,
    MAX_Z,
  );
  return { z, x: -b.x + (vw / z - b.w) / 2, y: -b.y + (vh / z - b.h) / 2 };
}

export const cssTransform = (c: Camera) => `translate(${c.x * c.z}px, ${c.y * c.z}px) scale(${c.z})`;
